import { ENV } from '../../../config/runtimeEnv';
import { Test, TestStats, TestResult, TestOrderPrintData } from "../../../types/healthcare/TestTypes";
import type { UserUuid } from "../../../types/identity";
import { http } from "../../../lib/http/client";

const HEALTHCARE_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

// Fetch all test orders for a given patient
export const fetchTests = async (patientId: UserUuid): Promise<Test[]> => {
	const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/${patientId}/test-orders`);
	return response.data;
};

// Fetch all test orders 
export interface TableFilterSort {
	filters?: Record<string, any>;
	sorter?: {
		field: string;
		order: 'ascend' | 'descend';
	};
}

export const fetchTestsCompany = async (
	page_number: number,
	rows: number,
	search?: string,
	tableParams?: TableFilterSort
): Promise<{ data: Test[]; total: number }> => {
	const params = {
		page_number,
		rows,
		search,
		filters: tableParams?.filters ? JSON.stringify(tableParams.filters) : undefined,
		sort_field: tableParams?.sorter?.field,
		sort_order: tableParams?.sorter?.order
	};

	const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/test-orders`, {
		params,
	});
	return { data: response.data.data, total: response.data.total };
};

// Fetch a single test order by ID
export const fetchTest = async (id: number): Promise<Test> => {
	const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/test-orders/${id}`);
	return response.data;
};

export const fetchTestOrderPrintData = async (
	orderIds: number[],
	options?: { includeMedicare?: boolean }
): Promise<TestOrderPrintData[]> => {
	const response = await http.post(
		`${HEALTHCARE_BASE_URL}/patients/test-orders/print-data`,
		{ order_ids: orderIds, include_medicare: options?.includeMedicare ?? false }
	);
	return response.data;
};

// Create a new test order
export const createTest = async (data: Partial<Test>): Promise<Test> => {
	const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/test-orders`, data);
	return response.data;
};

// A blood-report upload response. The collection counts a cumulative report yields are
// NOT here: BloodTestAnalysisJob discovers them minutes later and notifies the practitioner.
export interface TestUploadResponse {
	test: Test;
	test_result?: TestResult;
	message: string;
}

// Create a new test order with initial results (practitioner upload)
export const createTestWithResults = async (data: FormData): Promise<TestUploadResponse> => {
	const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/test-orders/with-results`, 
		data
	);
	return response.data;
};

// Update an existing test order
export const updateTest = async (id: number, data: Partial<Test>): Promise<Test> => {
	const response = await http.put(`${HEALTHCARE_BASE_URL}/patients/test-orders/${id}`, data);
	return response.data;
};

// Delete a test order
export const deleteTest = async (id: number): Promise<{ message: string }> => {
	const response = await http.delete(`${HEALTHCARE_BASE_URL}/patients/test-orders/${id}`);
	return response.data;
};

// Cancel a test order
export const cancelTest = async (id: number, reason: string): Promise<Test> => {
	const response = await http.put(`${HEALTHCARE_BASE_URL}/patients/test-orders/${id}/cancel`, { reason });
	return response.data;
};

// Add test results for a specific test type
export const addTestResults = async (id: number, testType: string, results: FormData): Promise<TestUploadResponse> => {
	// Append the test_type to the FormData
	results.append("test_type", testType);
	
	const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/test-orders/${id}/results`, 
		results
	);
	return response.data;
};

// Add test results for a specific test type
// One order per uploaded FILE, never per collection — see TestUploadResponse.
export const patientUploadTestResults = async (patientId: UserUuid, testType: string, results: FormData): Promise<{ tests: Test[]; test_results: TestResult[]; message: string }> => {
	// Only append test_type if not already present in FormData
	if (!results.has("test_type")) {
		results.append("test_type", testType);
	}
	
	const response = await http.post(`${HEALTHCARE_BASE_URL}/patient/${patientId}/results-upload`, 
		results
	);
	return response.data;
};


// Re-run the AI analysis for an order whose analysis failed. Returns the reloaded
// order (back in `in_progress`).
export const retryTestOrderAnalysis = async (id: number): Promise<Test> => {
	const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/test-orders/${id}/retry-analysis`, {});
	return response.data;
};


// Mark a test order as reviewed
export const markTestAsReviewed = async (id: number, notes?: string): Promise<Test> => {
	const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/test-orders/${id}/mark-reviewed`,
		{
			review_date: new Date(),
			review_notes: notes
		}
	);
	return response.data;
};


// Assign existing test order to another patient by sample ID
export const assignTestToPatient = async (sampleId: string, patientId: UserUuid): Promise<{ message: string; test: Test; original_patient_id: UserUuid }> => {
	const response = await http.post(`${HEALTHCARE_BASE_URL}/patients/test-orders/assign-to-patient`, {
		sample_id: sampleId,
		patient_id: patientId
	});
	return response.data;
};